import { useState, useRef, useCallback, useEffect } from 'react';
import { useBackgroundedTracker } from './useBackgroundedTracker';
import { isIntentionallySilent, shouldIgnoreGhostKick, ghostKickDisposition } from './voiceCallLiveness';
import { createFileShareChannel, attachFileShareReceiver, sendFileOverChannels, MAX_FILE_SHARE_BYTES, } from '../utils/callFileShare';
const ICE_SERVERS = [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    // TURN fallback (free Open Relay project) — without a relay, direct
    // phone-to-phone connections often fail behind carrier/symmetric NATs,
    // which shows up as "two mobile participants can't see each other" while
    // both still connect to a desktop peer.
    {
        urls: [
            'turn:openrelay.metered.ca:80',
            'turn:openrelay.metered.ca:443',
            'turns:openrelay.metered.ca:443?transport=tcp',
        ],
        username: 'openrelayproject',
        credential: 'openrelayproject',
    },
];
/** How many ICE-restart attempts to make before dropping a failed peer. */
const MAX_ICE_RESTARTS = 2;
/** Watchdog: how often to check peer connection health while in a call. */
const WATCHDOG_INTERVAL_MS = 4_000;
/** Watchdog: a peer stuck in new/connecting/checking longer than this is rebuilt. */
const CONNECT_TIMEOUT_MS = 12_000;
/** Watchdog: a peer in 'disconnected' longer than this is rebuilt. */
const DISCONNECTED_TIMEOUT_MS = 8_000;
/** Watchdog: connected but no media stream received within this → rebuild. */
const NO_MEDIA_TIMEOUT_MS = 8_000;
/** Minimum wait between two rebuild attempts towards the same peer (grows with attempts). */
const RECONNECT_BACKOFF_BASE_MS = 10_000;
const RECONNECT_BACKOFF_MAX_MS = 30_000;
/** Presence beacon: while in-call, rebroadcast `join` this often. Discovery
 *  signals can be silently lost (mobile browsers suspend the WebSocket during
 *  the camera/mic permission prompt or app switches); the beacon is idempotent
 *  and heals any lost `join`/`present` within one interval. */
const PRESENCE_BEACON_INTERVAL_MS = 8_000;
/** Ongoing-call discovery (for devices NOT in the call): the info expires if
 *  no beacon/probe answer refreshed it within this window (~2.5 beacons). */
const ONGOING_CALL_TTL_MS = 20_000;
/** How often idle devices re-check whether the ongoing-call info expired. */
const ONGOING_CALL_CHECK_MS = 5_000;
/** Video call: rebuild if no inbound video bytes for this long (audio may still flow). */
const NO_VIDEO_TIMEOUT_MS = 10_000;
/** Ghost participant: a peer that NEVER delivered any media (not a single
 *  inbound RTP byte) since it first appeared — e.g. a forgotten background
 *  tab whose camera/mic is held by another window. After this long (with at
 *  least one rebuild attempt already behind it, see CONNECT_TIMEOUT_MS), it
 *  is removed from the grid instead of being retried forever. Distinct from
 *  the rebuild logic: peers whose media EVER flowed are never ghost-removed. */
const GHOST_TIMEOUT_MS = 25_000;
/** Fresh health bookkeeping for a (re)created peer link. Rebuild counters are
 *  carried over from the previous entry so backoff keeps growing. */
const freshHealth = (prev) => ({
    createdAt: Date.now(),
    connectedAt: null,
    disconnectedAt: null,
    rebuilds: prev?.rebuilds ?? 0,
    lastRebuildAt: prev?.lastRebuildAt ?? 0,
    lastInboundBytes: 0,
    lastMediaAt: null,
    lastVideoBytes: 0,
    lastVideoAt: null,
    videoRebuilds: prev?.videoRebuilds ?? 0,
    firstSeenAt: prev?.firstSeenAt ?? Date.now(),
    everHadMedia: prev?.everHadMedia ?? false,
});
/** sessionStorage key holding the ongoing/left call info, so an accidental
 *  page refresh doesn't permanently kick the user out of a group call. */
const REJOIN_STORAGE_KEY = 'dw-voice-call-rejoin';
function saveRejoinEntry(callId, video) {
    try {
        sessionStorage.setItem(REJOIN_STORAGE_KEY, JSON.stringify({ callId, video, ts: Date.now() }));
    }
    catch { /* storage unavailable — rejoin just won't survive a refresh */ }
}
function clearRejoinEntry() {
    try {
        sessionStorage.removeItem(REJOIN_STORAGE_KEY);
    }
    catch { /* ignore */ }
}
function loadRejoinEntry() {
    try {
        const raw = sessionStorage.getItem(REJOIN_STORAGE_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed?.callId !== 'string' || typeof parsed?.ts !== 'number')
            return null;
        return { callId: parsed.callId, video: !!parsed.video, ts: parsed.ts };
    }
    catch {
        return null;
    }
}
/**
 * Bandwidth/quality adaptation tiers for mesh video (spec §3.3): in a mesh
 * every participant uploads a separate stream to every peer, so as the group
 * grows we lower per-stream bitrate, resolution and framerate to keep the
 * total upload manageable (esp. on mobile).
 */
/**
 * On mobile the camera + N mesh encoders are the main battery/heat drain, so
 * the capture ceiling is much lower than on desktop: 640x360@24 is plenty for
 * a phone-sized screen and roughly quarters the encode work vs 720p30.
 */
function isMobileDevice() {
    return (typeof navigator !== 'undefined' &&
        (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)));
}
/** Ideal camera constraints for a fresh capture (device-aware ceiling). */
function idealCameraConstraints(facingMode) {
    return isMobileDevice()
        ? { facingMode, width: { ideal: 640 }, height: { ideal: 360 }, frameRate: { ideal: 24 } }
        : { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } };
}
const VIDEO_QUALITY_TIERS = [
    // 1 peer (2 participants): full quality — ~720p, 30 fps
    { maxPeers: 1, maxBitrate: 1_200_000, scaleResolutionDownBy: 1, maxFramerate: 30, captureWidth: 1280, captureHeight: 720, captureFps: 30 },
    // 2 peers (3 participants): ~480p, 25 fps
    { maxPeers: 2, maxBitrate: 700_000, scaleResolutionDownBy: 1.5, maxFramerate: 25, captureWidth: 960, captureHeight: 540, captureFps: 25 },
    // 3 peers (4 participants): ~360p, 18 fps
    { maxPeers: 3, maxBitrate: 380_000, scaleResolutionDownBy: 2, maxFramerate: 18, captureWidth: 640, captureHeight: 360, captureFps: 20 },
    // 4 peers (5 participants): ~270p, 15 fps
    { maxPeers: 4, maxBitrate: 260_000, scaleResolutionDownBy: 2.5, maxFramerate: 15, captureWidth: 640, captureHeight: 360, captureFps: 15 },
    // 5+ peers (6+ participants): ~240p, 12 fps
    { maxPeers: Infinity, maxBitrate: 180_000, scaleResolutionDownBy: 3, maxFramerate: 12, captureWidth: 480, captureHeight: 270, captureFps: 12 },
];
function genCallId() {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
        return `call-${crypto.randomUUID()}`;
    }
    return `call-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
/**
 * Voice-only (audio) group call over WebRTC, using the shared chat WebSocket
 * as the signaling channel. Mesh topology: every participant holds a direct
 * peer connection to every other participant.
 *
 * Mesh join rule (avoids offer glare): existing participants send the OFFER to
 * a newcomer. The newcomer never offers — it only answers. So when a device
 * joins (broadcasts `join`), each device already in the call creates a peer
 * connection and offers to the newcomer.
 */
export function useVoiceCall({ deviceId, isConnected, sendCallSignal, canReceiveFile, }) {
    // Bump this when call logic changes — lets us verify on a device (console)
    // that it is actually running the latest bundle, not a stale PWA cache.
    useEffect(() => {
        console.log('[voiceCall] logic version: watchdog-v5 (ghost-participant removal)');
    }, []);
    const wasRecentlyBackgrounded = useBackgroundedTracker();
    const [callState, setCallState] = useState('idle');
    const [transportConnected, setTransportConnected] = useState(false);
    const [participants, setParticipants] = useState([]);
    const [incomingFrom, setIncomingFrom] = useState(null);
    const [callId, setCallId] = useState(null);
    const [isMuted, setIsMuted] = useState(false);
    /** Host toggle state: true after mute-all until the host withdraws it. */
    const [muteAllActive, setMuteAllActive] = useState(false);
    /** Raise-hand: own hand + the set of participants with raised hands. */
    const [handRaised, setHandRaised] = useState(false);
    const handRaisedRef = useRef(false);
    const [raisedHands, setRaisedHands] = useState([]);
    const [isVideoCall, setIsVideoCall] = useState(false);
    const [incomingVideo, setIncomingVideo] = useState(false);
    const [isCameraOff, setIsCameraOff] = useState(false);
    const [isScreenSharing, setIsScreenSharing] = useState(false);
    const [screenShareOwnerId, setScreenShareOwnerId] = useState(null);
    const [screenShareClaimPending, setScreenShareClaimPending] = useState(false);
    const [localStream, setLocalStream] = useState(null);
    const localStreamRef = useRef(null);
    /**
     * The mesh sender swaps the video track in localStreamRef to the display
     * capture while sharing. Keep an independently owned camera track for the
     * self-preview instead of ever exposing that outgoing screen track through
     * the public localStream value.
     */
    const cameraPreviewStreamRef = useRef(null);
    const cameraPreviewTrackRef = useRef(null);
    const peersRef = useRef(new Map());
    const pendingCandidatesRef = useRef(new Map());
    const callIdRef = useRef(null);
    const callStateRef = useRef('idle');
    const isVideoCallRef = useRef(false);
    const incomingVideoRef = useRef(false);
    const isMutedRef = useRef(false);
    // True while our mic is muted BECAUSE the host asked (mute-request). Only
    // such a mute may be undone remotely by the host's unmute-request — a
    // manual mute (or manual re-mute after the request) stays private.
    const mutedByHostRef = useRef(false);
    const isCameraOffRef = useRef(false);
    const facingRef = useRef('user');
    const isScreenSharingRef = useRef(false);
    const screenShareOwnerRef = useRef(null);
    const screenShareOwnerByCallRef = useRef(new Map());
    const screenShareClaimPendingRef = useRef(false);
    const screenShareClaimTimerRef = useRef(null);
    const startScreenShareAfterClaimRef = useRef(null);
    const pendingScreenTrackRef = useRef(null);
    /** Serializes screen-share start/stop transitions; prevents duplicate
     *  camera re-acquisitions when the onended callback fires concurrently
     *  with a UI-driven stopScreenShare call. */
    const screenShareTransitionRef = useRef(false);
    /** Invalidates an in-flight screen-share transition when the call ends.
     *  A late replaceTrack resolution must never attach a newly acquired track
     *  to a stream that teardown has already detached. */
    const screenShareOperationRef = useRef(0);
    const micDeviceIdRef = useRef(null);
    const micSwitchGenerationRef = useRef(0);
    /** Devices that joined the call while we were still ringing (accepting).
     *  Their `join` arrived before we reached 'in-call', so we must greet them
     *  after accept — otherwise two callees accepting at the same time never
     *  discover each other (each misses the other's join). */
    const pendingJoinersRef = useRef(new Set());
    /** Per-peer count of ICE restart attempts after a failed connection. */
    const iceRestartsRef = useRef(new Map());
    /** Per-peer connection health bookkeeping for the watchdog. */
    const peerHealthRef = useRef(new Map());
    /** Peers that signalled their camera is intentionally off — the watchdog
     *  must not treat their missing video as a failure and rebuild forever. */
    const remoteCameraOffRef = useRef(new Set());
    /** Peers that signalled their mic is muted (UI badge). Kept in refs so a
     *  media-state arriving BEFORE the participant entry exists is not lost —
     *  the flags are applied when the participant is (re)created. */
    const remoteMicMutedRef = useRef(new Set());
    /** Mirror of the participants state for use inside timers. */
    const participantsRef = useRef([]);
    /** Info about the call we voluntarily left while others were still in it,
     *  so the user can rejoin. Cleared on rejoin or when a new call replaces it. */
    const rejoinInfoRef = useRef(null);
    const rejoinProbeTimerRef = useRef(null);
    const [canRejoin, setCanRejoin] = useState(false);
    const [rejoinGeneration, setRejoinGeneration] = useState(0);
    /** Call running in the room that we are not part of (probe/beacon result). */
    const [ongoingCall, setOngoingCall] = useState(null);
    /** We were kicked from a call as a "ghost" (our media never reached anyone). */
    const [callDropped, setCallDropped] = useState(false);
    /** True when the drop was (very likely) caused by backgrounding the app. */
    const [callDroppedByBackground, setCallDroppedByBackground] = useState(false);
    const ongoingCallRef = useRef(null);
    const ongoingSeenAtRef = useRef(0);
    const sendRef = useRef(sendCallSignal);
    const deviceIdRef = useRef(deviceId);
    /** Per-peer file-share data channels (negotiated, created in createPeer). */
    const fileChannelsRef = useRef(new Map());
    const [fileTransfers, setFileTransfers] = useState([]);
    const upsertTransfer = useCallback((tr) => {
        setFileTransfers((prev) => {
            const idx = prev.findIndex((p) => p.id === tr.id && p.direction === tr.direction);
            if (idx === -1)
                return [...prev, tr];
            const old = prev[idx];
            // never lose an already-created blob URL on a late progress update
            const merged = { ...old, ...tr, blobUrl: tr.blobUrl || old.blobUrl };
            const next = prev.slice();
            next[idx] = merged;
            return next;
        });
    }, []);
    sendRef.current = sendCallSignal;
    deviceIdRef.current = deviceId;
    const setCallIdBoth = (id) => {
        callIdRef.current = id;
        setCallId(id);
        const owner = id ? (screenShareOwnerByCallRef.current.get(id) ?? null) : null;
        screenShareOwnerRef.current = owner;
        setScreenShareOwnerId(owner);
        setParticipants((prev) => prev.map((participant) => ({
            ...participant,
            isScreenSharing: participant.deviceId === owner,
        })));
    };
    const setCallStateBoth = (s) => {
        callStateRef.current = s;
        setCallState(s);
    };
    const setIsVideoCallBoth = (v) => {
        isVideoCallRef.current = v;
        setIsVideoCall(v);
    };
    const setLocalStreamBoth = (s) => {
        localStreamRef.current = s;
        setLocalStream(s);
    };
    const stopCameraPreview = useCallback((expectedStream) => {
        // A picker belonging to an older call may settle after teardown has
        // already started a new call. Never let that stale completion stop the
        // replacement call's camera preview.
        if (expectedStream !== undefined &&
            cameraPreviewStreamRef.current !== expectedStream) {
            return;
        }
        const previewTrack = cameraPreviewTrackRef.current;
        cameraPreviewTrackRef.current = null;
        cameraPreviewStreamRef.current = null;
        // The preview stream also contains the call's audio track, which is owned
        // by localStreamRef. Only stop the cloned video track here.
        if (previewTrack) {
            previewTrack.stop();
        }
    }, []);
    const createCameraPreview = useCallback((local) => {
        const cameraTrack = local.getVideoTracks()[0];
        if (!cameraTrack || typeof cameraTrack.clone !== 'function')
            return null;
        let previewTrack;
        try {
            previewTrack = cameraTrack.clone();
        }
        catch (err) {
            console.warn('[voiceCall] camera preview clone failed:', err);
            return null;
        }
        // A broken test double or an unusual browser implementation must not
        // cause teardown to stop the live outgoing camera track.
        if (previewTrack === cameraTrack)
            return null;
        previewTrack.enabled = cameraTrack.enabled;
        let preview;
        try {
            preview = new MediaStream();
            local.getAudioTracks().forEach((track) => preview.addTrack(track));
            preview.addTrack(previewTrack);
        }
        catch (err) {
            previewTrack.stop();
            console.warn('[voiceCall] camera preview MediaStream construction failed; continuing without self-preview:', err);
            return null;
        }
        cameraPreviewStreamRef.current = preview;
        cameraPreviewTrackRef.current = previewTrack;
        return preview;
    }, []);
    // Uplink-congestion adaptation state: the watchdog inspects candidate-pair
    // availableOutgoingBitrate each tick; sustained shortage steps the video
    // quality down one tier at a time (fast), sustained headroom steps back up
    // (slow). Keeps a weak-mobile-data participant sending audio+video instead
    // of choking the connection.
    const netPenaltyRef = useRef(0);
    const netBadTicksRef = useRef(0);
    const netGoodTicksRef = useRef(0);
    /** Mobile thermal guard: the worst tier reached in this call. */
    const mobileWorstTierRef = useRef(0);
    /** Acquire a replacement camera at the already-latched tier rather than
     * briefly reopening it at the mobile default and downscaling later. */
    const cameraConstraintsForCurrentTier = useCallback((facingMode) => {
        if (!isMobileDevice())
            return idealCameraConstraints(facingMode);
        const tier = VIDEO_QUALITY_TIERS[mobileWorstTierRef.current];
        return {
            facingMode,
            width: { ideal: Math.min(tier.captureWidth, 640) },
            height: { ideal: Math.min(tier.captureHeight, 360) },
            frameRate: { ideal: Math.min(tier.captureFps, 24), max: tier.maxFramerate },
        };
    }, []);
    const ensureLocalStream = useCallback(async (video) => {
        if (localStreamRef.current)
            return localStreamRef.current;
        const constraints = video
            ? {
                audio: true,
                video: cameraConstraintsForCurrentTier(facingRef.current),
            }
            : { audio: true, video: false };
        let stream;
        try {
            stream = await navigator.mediaDevices.getUserMedia(constraints);
        }
        catch (cameraError) {
            if (!video)
                throw cameraError;
            // A missing/busy/blocked camera must not prevent a guest from joining a
            // video room. Retry in audio-only mode; the camera button remains
            // available so the user can enable video later after fixing the device.
            console.warn('[voiceCall] Camera unavailable; joining audio-only:', cameraError);
            stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            isCameraOffRef.current = true;
            setIsCameraOff(true);
        }
        // apply current mute/camera state to the fresh tracks
        stream.getAudioTracks().forEach((t) => { t.enabled = !isMutedRef.current; });
        stream.getVideoTracks().forEach((t) => { t.enabled = !isCameraOffRef.current; });
        setLocalStreamBoth(stream);
        return stream;
    }, [cameraConstraintsForCurrentTier]);
    /**
     * Bandwidth / quality adaptation (spec §3.3). Picks the quality tier for the
     * current mesh size and applies it to every outgoing video sender via
     * RTCRtpSender.setParameters() — no renegotiation needed. Called whenever
     * the peer count changes and after a camera switch.
     */
    const applyVideoQuality = useCallback(() => {
        if (!isVideoCallRef.current)
            return;
        const peerCount = peersRef.current.size;
        if (peerCount === 0)
            return;
        // Base tier from group size, then step further down while the network
        // penalty (uplink congestion detected by the watchdog) is active.
        const baseIdx = VIDEO_QUALITY_TIERS.findIndex((t) => peerCount <= t.maxPeers);
        const computedIdx = Math.min(baseIdx + netPenaltyRef.current, VIDEO_QUALITY_TIERS.length - 1);
        if (isMobileDevice()) {
            mobileWorstTierRef.current = Math.max(mobileWorstTierRef.current, computedIdx);
        }
        const idx = isMobileDevice() ? mobileWorstTierRef.current : computedIdx;
        const tier = VIDEO_QUALITY_TIERS[idx];
        // Screen shares are a separate readability-critical source: never feed
        // their sender the thermal camera tier.
        const senderTier = isScreenSharingRef.current ? VIDEO_QUALITY_TIERS[0] : tier;
        peersRef.current.forEach((pc) => {
            pc.getSenders().forEach((sender) => {
                if (!sender.track || sender.track.kind !== 'video')
                    return;
                const params = sender.getParameters();
                if (!params.encodings || params.encodings.length === 0) {
                    params.encodings = [{}];
                }
                const enc = params.encodings[0];
                enc.maxBitrate = senderTier.maxBitrate;
                enc.scaleResolutionDownBy = senderTier.scaleResolutionDownBy;
                enc.maxFramerate = senderTier.maxFramerate;
                sender
                    .setParameters(params)
                    .catch((err) => console.warn('[voiceCall] setParameters failed:', err));
            });
        });
        // Capture-side downscale: with N peers the encoder runs N times on the
        // same frame, so lowering the camera resolution/fps saves CPU too — this
        // is what actually keeps 4-5 participant calls smooth.
        const local = localStreamRef.current;
        if (local && !isScreenSharingRef.current) {
            // Mobile ceiling: never ask the camera for more than 640x360@24 — the
            // encode cost of anything higher is the main phone-heating culprit.
            const capW = isMobileDevice() ? Math.min(tier.captureWidth, 640) : tier.captureWidth;
            const capH = isMobileDevice() ? Math.min(tier.captureHeight, 360) : tier.captureHeight;
            const capFps = isMobileDevice() ? Math.min(tier.captureFps, 24) : tier.captureFps;
            local.getVideoTracks().forEach((track) => {
                track
                    .applyConstraints({
                    width: { ideal: capW },
                    height: { ideal: capH },
                    frameRate: { ideal: capFps, max: tier.maxFramerate },
                })
                    .catch((err) => console.warn('[voiceCall] applyConstraints failed:', err));
            });
        }
    }, []);
    /** Watchdog feed: step video quality down on sustained uplink shortage, back up on sustained headroom. */
    const adaptToUplink = useCallback((availableOut) => {
        if (availableOut === null || !isVideoCallRef.current)
            return;
        const peerCount = peersRef.current.size;
        if (peerCount === 0)
            return;
        const baseIdx = VIDEO_QUALITY_TIERS.findIndex((t) => peerCount <= t.maxPeers);
        const idx = Math.min(baseIdx + netPenaltyRef.current, VIDEO_QUALITY_TIERS.length - 1);
        const need = VIDEO_QUALITY_TIERS[idx].maxBitrate;
        if (availableOut < need * 0.8) {
            netGoodTicksRef.current = 0;
            if (++netBadTicksRef.current >= 2 && idx < VIDEO_QUALITY_TIERS.length - 1) {
                netBadTicksRef.current = 0;
                netPenaltyRef.current += 1;
                console.log(`[voiceCall] uplink congested (${Math.round(availableOut / 1000)}kbps < ${Math.round(need / 1000)}kbps) — lowering video quality (penalty ${netPenaltyRef.current})`);
                applyVideoQuality();
            }
        }
        else if (!isMobileDevice() &&
            netPenaltyRef.current > 0 &&
            availableOut > VIDEO_QUALITY_TIERS[idx - 1].maxBitrate * 1.3) {
            // Recovery is judged against the NEXT HIGHER tier (with 30% headroom),
            // otherwise a link that could sustain the higher tier stays pinned low.
            netBadTicksRef.current = 0;
            if (++netGoodTicksRef.current >= 12) {
                netGoodTicksRef.current = 0;
                netPenaltyRef.current -= 1;
                console.log(`[voiceCall] uplink recovered — raising video quality (penalty ${netPenaltyRef.current})`);
                applyVideoQuality();
            }
        }
        else {
            netBadTicksRef.current = 0;
        }
    }, [applyVideoQuality]);
    const refreshTransportConnected = useCallback(() => {
        const peers = [...peersRef.current.values()];
        setTransportConnected(peers.length > 0 && peers.every((peer) => peer.connectionState === 'connected'));
    }, []);
    const removeParticipant = useCallback((remoteId) => {
        const pc = peersRef.current.get(remoteId);
        if (pc) {
            try {
                pc.onicecandidate = null;
                pc.ontrack = null;
                pc.onconnectionstatechange = null;
                pc.close();
            }
            catch { /* ignore */ }
            peersRef.current.delete(remoteId);
        }
        const dc = fileChannelsRef.current.get(remoteId);
        if (dc) {
            try {
                dc.close();
            }
            catch { /* ignore */ }
            fileChannelsRef.current.delete(remoteId);
        }
        iceRestartsRef.current.delete(remoteId);
        peerHealthRef.current.delete(remoteId);
        pendingCandidatesRef.current.delete(remoteId);
        remoteCameraOffRef.current.delete(remoteId);
        remoteMicMutedRef.current.delete(remoteId);
        // A departed participant's raised hand must not stay badged.
        setRaisedHands((prev) => prev.filter((id) => id !== remoteId));
        setParticipants((prev) => prev.filter((p) => p.deviceId !== remoteId));
        refreshTransportConnected();
        // fewer peers → we can afford higher quality for the remaining ones
        applyVideoQuality();
    }, [applyVideoQuality, refreshTransportConnected]);
    const teardown = useCallback((opts) => {
        const activeCallId = callIdRef.current;
        const pendingScreenTrack = pendingScreenTrackRef.current;
        pendingScreenTrackRef.current = null;
        if (pendingScreenTrack) {
            pendingScreenTrack.onended = null;
            pendingScreenTrack.stop();
        }
        if (isScreenSharingRef.current) {
            const activeScreenTrack = localStreamRef.current?.getVideoTracks()[0];
            if (activeScreenTrack) {
                activeScreenTrack.onended = null;
                activeScreenTrack.stop();
            }
        }
        if (screenShareOwnerRef.current === deviceIdRef.current && activeCallId) {
            sendRef.current({ subtype: 'screen-share-release', callId: activeCallId });
            screenShareOwnerByCallRef.current.set(activeCallId, null);
        }
        if (screenShareClaimTimerRef.current) {
            clearTimeout(screenShareClaimTimerRef.current);
            screenShareClaimTimerRef.current = null;
        }
        screenShareClaimPendingRef.current = false;
        setScreenShareClaimPending(false);
        screenShareOwnerRef.current = null;
        setScreenShareOwnerId(null);
        screenShareOperationRef.current += 1;
        screenShareTransitionRef.current = false;
        stopCameraPreview();
        peersRef.current.forEach((pc) => {
            try {
                pc.onicecandidate = null;
                pc.ontrack = null;
                pc.onconnectionstatechange = null;
                pc.close();
            }
            catch { /* ignore */ }
        });
        peersRef.current.clear();
        setTransportConnected(false);
        fileChannelsRef.current.forEach((dc) => { try {
            dc.close();
        }
        catch { /* ignore */ } });
        fileChannelsRef.current.clear();
        setFileTransfers((prev) => {
            prev.forEach((tr) => { if (tr.blobUrl)
                URL.revokeObjectURL(tr.blobUrl); });
            return [];
        });
        pendingCandidatesRef.current.clear();
        pendingJoinersRef.current.clear();
        iceRestartsRef.current.clear();
        peerHealthRef.current.clear();
        if (!opts?.preserveRejoinStorage)
            clearRejoinEntry();
        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach((t) => t.stop());
        }
        setLocalStreamBoth(null);
        remoteCameraOffRef.current.clear();
        remoteMicMutedRef.current.clear();
        setParticipants([]);
        setIncomingFrom(null);
        isMutedRef.current = false;
        mutedByHostRef.current = false;
        setIsMuted(false);
        setMuteAllActive(false);
        handRaisedRef.current = false;
        setHandRaised(false);
        setRaisedHands([]);
        isCameraOffRef.current = false;
        setIsCameraOff(false);
        incomingVideoRef.current = false;
        setIncomingVideo(false);
        facingRef.current = 'user';
        netPenaltyRef.current = 0;
        netBadTicksRef.current = 0;
        netGoodTicksRef.current = 0;
        mobileWorstTierRef.current = 0;
        isScreenSharingRef.current = false;
        setIsScreenSharing(false);
        setIsVideoCallBoth(false);
        setCallIdBoth(null);
        setCallStateBoth('idle');
    }, [stopCameraPreview]);
    /**
     * End the call locally because our own links died (all peers failed/closed,
     * or we were ghost-kicked). When the loss coincides with the app being
     * backgrounded (mobile app switch), the others are very likely still in the
     * call: surface the "you left the app" notice and keep the rejoin window
     * open. Otherwise fall back to the previous behavior — `alwaysNotify`
     * decides whether the generic dropped notice is shown.
     */
    const dropFromCall = useCallback((alwaysNotify) => {
        const wasBackground = wasRecentlyBackgrounded();
        const droppedCallId = callIdRef.current;
        const droppedVideo = isVideoCallRef.current;
        teardown();
        if (wasBackground && droppedCallId) {
            rejoinInfoRef.current = { callId: droppedCallId, video: droppedVideo };
            setRejoinGeneration((value) => value + 1);
            setCanRejoin(true);
            saveRejoinEntry(droppedCallId, droppedVideo);
            setCallDroppedByBackground(true);
            setCallDropped(true);
        }
        else {
            clearRejoinEntry();
            setCallDroppedByBackground(false);
            if (alwaysNotify)
                setCallDropped(true);
        }
    }, [teardown, wasRecentlyBackgrounded]);
    const createPeer = useCallback((remoteId) => {
        const existing = peersRef.current.get(remoteId);
        if (existing)
            return existing;
        const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
        // File-share data channel — negotiated (same id on both sides), so it
        // exists no matter which peer created the SDP offer. Recreated together
        // with the pc on watchdog rebuilds.
        try {
            const dc = createFileShareChannel(pc);
            attachFileShareReceiver(dc, remoteId, upsertTransfer);
            fileChannelsRef.current.set(remoteId, dc);
        }
        catch (err) {
            console.warn('[voiceCall] file-share channel creation failed:', err);
        }
        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach((track) => {
                pc.addTrack(track, localStreamRef.current);
            });
        }
        pc.onicecandidate = (e) => {
            if (e.candidate && callIdRef.current) {
                sendRef.current({
                    subtype: 'ice',
                    callId: callIdRef.current,
                    to: remoteId,
                    payload: e.candidate.toJSON(),
                });
            }
        };
        pc.ontrack = (e) => {
            const stream = e.streams[0];
            setParticipants((prev) => {
                const found = prev.find((p) => p.deviceId === remoteId);
                if (found) {
                    return prev.map((p) => (p.deviceId === remoteId ? { ...p, stream } : p));
                }
                // apply any media-state that arrived before this participant existed
                return [...prev, {
                        deviceId: remoteId,
                        stream,
                        isScreenSharing: screenShareOwnerRef.current === remoteId,
                        cameraOff: remoteCameraOffRef.current.has(remoteId),
                        micMuted: remoteMicMutedRef.current.has(remoteId),
                    }];
            });
        };
        pc.onconnectionstatechange = () => {
            console.log(`[voiceCall] peer ${remoteId} connection: ${pc.connectionState}`);
            refreshTransportConnected();
            const health = peerHealthRef.current.get(remoteId);
            if (health && peersRef.current.get(remoteId) === pc) {
                if (pc.connectionState === 'connected') {
                    health.connectedAt = Date.now();
                    health.disconnectedAt = null;
                }
                else if (pc.connectionState === 'disconnected') {
                    health.disconnectedAt = Date.now();
                }
                else {
                    health.disconnectedAt = null;
                }
            }
            if (pc.connectionState === 'connected') {
                iceRestartsRef.current.delete(remoteId);
                // re-apply after negotiation — pre-negotiation setParameters may not
                // survive SDP application in every browser
                applyVideoQuality();
            }
            if (pc.connectionState === 'failed') {
                // Before dropping the peer, try an ICE restart — mobile networks and
                // NAT changes often kill the first candidate pair while a fresh
                // gathering round (now also with TURN) still succeeds. Only the
                // deterministic initiator side re-offers, to avoid glare.
                const attempts = iceRestartsRef.current.get(remoteId) || 0;
                if (attempts < MAX_ICE_RESTARTS &&
                    callStateRef.current === 'in-call' &&
                    peersRef.current.get(remoteId) === pc) {
                    iceRestartsRef.current.set(remoteId, attempts + 1);
                    if (deviceIdRef.current > remoteId) {
                        console.log(`[voiceCall] ICE restart #${attempts + 1} towards ${remoteId}`);
                        (async () => {
                            try {
                                const offer = await pc.createOffer({ iceRestart: true });
                                await pc.setLocalDescription(offer);
                                if (callIdRef.current) {
                                    sendRef.current({ subtype: 'offer', callId: callIdRef.current, to: remoteId, payload: offer });
                                }
                            }
                            catch (err) {
                                console.error('[voiceCall] ICE restart failed:', err);
                            }
                        })();
                    }
                    // the non-initiator side just waits for the restart offer
                    return;
                }
                removeParticipant(remoteId);
                if (peersRef.current.size === 0 && callStateRef.current === 'in-call') {
                    dropFromCall(false);
                }
            }
            if (pc.connectionState === 'closed') {
                removeParticipant(remoteId);
                // if we are now alone in the call, end it locally
                if (peersRef.current.size === 0 && callStateRef.current === 'in-call') {
                    dropFromCall(false);
                }
            }
        };
        peersRef.current.set(remoteId, pc);
        refreshTransportConnected();
        peerHealthRef.current.set(remoteId, freshHealth(peerHealthRef.current.get(remoteId)));
        setParticipants((prev) => prev.find((p) => p.deviceId === remoteId) ? prev : [...prev, { deviceId: remoteId, stream: null }]);
        // more peers → lower per-stream quality to keep total upload manageable
        applyVideoQuality();
        return pc;
    }, [removeParticipant, dropFromCall, applyVideoQuality, refreshTransportConnected, upsertTransfer]);
    /** Guard: one outgoing transfer at a time — overlapping sends on the same
     *  channel would interleave chunks and corrupt the receiver's stream. */
    const sendingFileRef = useRef(false);
    const canReceiveFileRef = useRef(canReceiveFile);
    canReceiveFileRef.current = canReceiveFile;
    const shareFile = useCallback((file, mode = 'download') => {
        if (file.size > MAX_FILE_SHARE_BYTES)
            return false;
        if (callStateRef.current !== 'in-call')
            return false;
        if (sendingFileRef.current)
            return false; // not accepted: keep the picker open
        // Recipient policy: e.g. chat-only guests never receive shared files.
        const allowed = new Map();
        fileChannelsRef.current.forEach((dc, id) => {
            if (!canReceiveFileRef.current || canReceiveFileRef.current(id))
                allowed.set(id, dc);
        });
        sendingFileRef.current = true;
        void sendFileOverChannels(allowed, file, upsertTransfer, mode)
            .finally(() => { sendingFileRef.current = false; });
        return true;
    }, [upsertTransfer]);
    const dismissTransfer = useCallback((id) => {
        setFileTransfers((prev) => {
            const tr = prev.find((p) => p.id === id);
            if (tr?.blobUrl)
                URL.revokeObjectURL(tr.blobUrl);
            return prev.filter((p) => p.id !== id);
        });
    }, []);
    const flushCandidates = useCallback(async (remoteId) => {
        const pc = peersRef.current.get(remoteId);
        const queued = pendingCandidatesRef.current.get(remoteId);
        if (!pc || !queued)
            return;
        for (const c of queued) {
            try {
                await pc.addIceCandidate(c);
            }
            catch (err) {
                console.error('[voiceCall] addIceCandidate failed:', err);
            }
        }
        pendingCandidatesRef.current.delete(remoteId);
    }, []);
    /**
     * Deterministic offer initiation for a peer pair, to avoid offer glare when
     * two devices discover each other simultaneously. Exactly one side initiates:
     * the device with the lexicographically greater deviceId. The other side
     * waits for the incoming offer and only answers.
     */
    const maybeInitiate = useCallback(async (remoteId) => {
        if (peersRef.current.has(remoteId))
            return; // already connecting/connected
        if (deviceIdRef.current <= remoteId)
            return; // the other side will offer
        const pc = createPeer(remoteId);
        try {
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            if (callIdRef.current) {
                sendRef.current({ subtype: 'offer', callId: callIdRef.current, to: remoteId, payload: offer });
            }
        }
        catch (err) {
            console.error('[voiceCall] createOffer failed:', err);
        }
    }, [createPeer]);
    /**
     * Close a peer connection but KEEP the participant entry (stream reset to
     * null), so the UI keeps showing the person as "connecting" while the
     * watchdog rebuilds the link.
     */
    const closePeer = useCallback((remoteId) => {
        const pc = peersRef.current.get(remoteId);
        if (pc) {
            try {
                pc.onicecandidate = null;
                pc.ontrack = null;
                pc.onconnectionstatechange = null;
                pc.close();
            }
            catch { /* ignore */ }
            peersRef.current.delete(remoteId);
        }
        iceRestartsRef.current.delete(remoteId);
        pendingCandidatesRef.current.delete(remoteId);
        setParticipants((prev) => prev.map((p) => (p.deviceId === remoteId ? { ...p, stream: null } : p)));
    }, []);
    /**
     * Full rebuild of a broken peer link (watchdog): tear down our side, tell
     * the other side to do the same (`reconnect` signal), then run the normal
     * deterministic offer initiation. Repeats with backoff until media flows —
     * "keep working on the connection until picture and sound appear".
     */
    const rebuildPeer = useCallback((remoteId, reason, videoTriggered = false) => {
        if (callStateRef.current !== 'in-call' || !callIdRef.current)
            return;
        const health = peerHealthRef.current.get(remoteId);
        const rebuilds = (health?.rebuilds ?? 0) + 1;
        console.warn(`[voiceCall] rebuilding link to ${remoteId} (#${rebuilds}, reason: ${reason})`);
        closePeer(remoteId);
        peerHealthRef.current.set(remoteId, {
            ...freshHealth(health),
            rebuilds,
            lastRebuildAt: Date.now(),
            videoRebuilds: (health?.videoRebuilds ?? 0) + (videoTriggered ? 1 : 0),
        });
        sendRef.current({ subtype: 'reconnect', callId: callIdRef.current, to: remoteId });
        void maybeInitiate(remoteId);
    }, [closePeer, maybeInitiate]);
    /**
     * Upgrade an audio-only local stream with a camera track (used when an
     * audio call merges into an ongoing VIDEO call). New peer connections pick
     * the track up automatically (createPeer adds all local tracks). If the
     * camera is unavailable, the device stays audio-only in the video call.
     */
    const upgradeLocalToVideo = useCallback(async () => {
        const local = localStreamRef.current;
        if (local && local.getVideoTracks().length === 0) {
            try {
                const cam = await navigator.mediaDevices.getUserMedia({
                    video: cameraConstraintsForCurrentTier(facingRef.current),
                    audio: false,
                });
                const track = cam.getVideoTracks()[0];
                // the call may have ended during the await — do not leak the camera
                if (track && localStreamRef.current === local && callStateRef.current === 'in-call') {
                    track.enabled = !isCameraOffRef.current;
                    local.addTrack(track);
                }
                else {
                    cam.getTracks().forEach((t) => t.stop());
                }
            }
            catch (err) {
                console.warn('[voiceCall] camera unavailable for video upgrade — staying audio-only:', err);
            }
        }
        setIsVideoCallBoth(true);
    }, [cameraConstraintsForCurrentTier]);
    /**
     * Merge our current call into another ongoing call in the room (parallel
     * call prevention — deterministic rule: the SMALLER callId always wins).
     * Any peers we already had are told to merge too, then all links are torn
     * down and rebuilt inside the target call via the normal join flow.
     */
    const mergeIntoCall = useCallback(async (targetCallId, video) => {
        if (callStateRef.current !== 'in-call' || !callIdRef.current)
            return;
        if (callIdRef.current === targetCallId)
            return;
        console.log(`[voiceCall] merging call ${callIdRef.current} into ongoing call ${targetCallId}`);
        // propagate the merge to devices connected to OUR (losing) call
        peersRef.current.forEach((_pc, remoteId) => {
            sendRef.current({ subtype: 'call-exists', callId: targetCallId, to: remoteId, payload: { video } });
        });
        peersRef.current.forEach((pc) => {
            try {
                pc.onicecandidate = null;
                pc.ontrack = null;
                pc.onconnectionstatechange = null;
                pc.close();
            }
            catch { /* ignore */ }
        });
        peersRef.current.clear();
        pendingCandidatesRef.current.clear();
        iceRestartsRef.current.clear();
        peerHealthRef.current.clear();
        remoteCameraOffRef.current.clear();
        remoteMicMutedRef.current.clear();
        setParticipants([]);
        setCallIdBoth(targetCallId);
        if (video && !isVideoCallRef.current) {
            await upgradeLocalToVideo();
        }
        if (callIdRef.current === targetCallId && callStateRef.current === 'in-call') {
            sendRef.current({ subtype: 'join', callId: targetCallId, payload: { video: isVideoCallRef.current } });
        }
    }, [upgradeLocalToVideo]);
    // --- public actions ---
    /** Record (or clear) the ongoing call discovered in the room. */
    const markOngoing = useCallback((info) => {
        ongoingSeenAtRef.current = Date.now();
        const prev = ongoingCallRef.current;
        ongoingCallRef.current = info;
        if (!prev || !info || prev.callId !== info.callId || prev.video !== info.video) {
            setOngoingCall(info);
        }
    }, []);
    const clearRejoin = useCallback(() => {
        rejoinInfoRef.current = null;
        setRejoinGeneration((value) => value + 1);
        setCanRejoin(false);
        clearRejoinEntry();
        if (rejoinProbeTimerRef.current) {
            clearTimeout(rejoinProbeTimerRef.current);
            rejoinProbeTimerRef.current = null;
        }
    }, []);
    const dismissCallDropped = useCallback(() => {
        setCallDropped(false);
        setCallDroppedByBackground(false);
    }, []);
    const startCall = useCallback(async (opts) => {
        if (callStateRef.current !== 'idle')
            return;
        const video = !!opts?.video;
        try {
            await ensureLocalStream(video);
        }
        catch (err) {
            console.error('[voiceCall] Media access denied:', err);
            return;
        }
        clearRejoin();
        setCallDropped(false);
        setCallDroppedByBackground(false);
        const id = genCallId();
        setIsVideoCallBoth(video);
        setCallIdBoth(id);
        setCallStateBoth('in-call');
        setParticipants([]);
        // announce to the whole room; idle devices will ring
        sendRef.current({ subtype: 'invite', callId: id, payload: { video } });
    }, [ensureLocalStream]);
    const acceptCall = useCallback(async () => {
        if (callStateRef.current !== 'incoming' || !callIdRef.current)
            return;
        const video = incomingVideoRef.current;
        try {
            await ensureLocalStream(video);
        }
        catch (err) {
            console.error('[voiceCall] Media access denied:', err);
            return;
        }
        clearRejoin();
        setCallDropped(false);
        setCallDroppedByBackground(false);
        setIsVideoCallBoth(video);
        setCallStateBoth('in-call');
        setIncomingFrom(null);
        // tell everyone we joined; existing participants will offer to us
        sendRef.current({ subtype: 'join', callId: callIdRef.current });
        // Greet devices whose `join` arrived while we were still ringing —
        // without this, two callees accepting simultaneously never connect.
        const pending = Array.from(pendingJoinersRef.current);
        pendingJoinersRef.current.clear();
        pending.forEach((remoteId) => {
            if (!callIdRef.current)
                return;
            sendRef.current({ subtype: 'present', callId: callIdRef.current, to: remoteId });
            void maybeInitiate(remoteId);
        });
    }, [ensureLocalStream, maybeInitiate]);
    const rejectCall = useCallback(() => {
        if (callStateRef.current !== 'incoming')
            return;
        if (callIdRef.current) {
            sendRef.current({ subtype: 'reject', callId: callIdRef.current });
        }
        setIncomingFrom(null);
        setCallIdBoth(null);
        setCallStateBoth('idle');
    }, []);
    const hangup = useCallback(() => {
        // If we leave while others are still connected, offer a rejoin window.
        const leftOngoing = callStateRef.current === 'in-call' &&
            peersRef.current.size > 0 &&
            !!callIdRef.current;
        const leftCallId = callIdRef.current;
        const leftVideo = isVideoCallRef.current;
        if (callIdRef.current) {
            sendRef.current({ subtype: 'leave', callId: callIdRef.current });
        }
        teardown();
        if (leftOngoing && leftCallId) {
            rejoinInfoRef.current = { callId: leftCallId, video: leftVideo };
            setRejoinGeneration((value) => value + 1);
            setCanRejoin(true);
            saveRejoinEntry(leftCallId, leftVideo);
        }
    }, [teardown]);
    const leaveCall = useCallback(() => {
        hangup();
        // A deliberate guest departure must not resurrect the just-left call after
        // a refresh. Keep this narrowly scoped to call state; chat/invite storage
        // belongs to the guest session and is intentionally untouched.
        clearRejoin();
        setCallDropped(false);
        setCallDroppedByBackground(false);
        markOngoing(null);
    }, [clearRejoin, hangup, markOngoing]);
    /**
     * Join an existing (ongoing) call by callId — shared by "rejoin the call I
     * left" and "join the ongoing call I discovered". Broadcasts `join`;
     * participants respond with `present`/offers like for any newcomer. If
     * nobody responds within a short probe window, the call is over: teardown.
     */
    const joinExistingCall = useCallback(async (info) => {
        if (callStateRef.current !== 'idle')
            return;
        let stream;
        try {
            stream = await ensureLocalStream(info.video);
        }
        catch (err) {
            console.error('[voiceCall] Media access denied:', err);
            return;
        }
        // state may have changed during the permission prompt (e.g. a new
        // incoming call rang) — release the freshly acquired media, don't leak it
        if (callStateRef.current !== 'idle') {
            // nobody else can have claimed this stream yet (accepting a new call
            // requires a user tap that happens after this check), so stop it
            stream.getTracks().forEach((t) => t.stop());
            if (localStreamRef.current === stream)
                setLocalStreamBoth(null);
            return;
        }
        clearRejoin();
        setCallDropped(false);
        setCallDroppedByBackground(false);
        markOngoing(null);
        setIsVideoCallBoth(info.video);
        setCallIdBoth(info.callId);
        setCallStateBoth('in-call');
        setParticipants([]);
        sendRef.current({ subtype: 'join', callId: info.callId, payload: { video: info.video } });
        // if nobody is left in the call, nothing will connect — give up after 8s
        rejoinProbeTimerRef.current = setTimeout(() => {
            rejoinProbeTimerRef.current = null;
            if (callStateRef.current === 'in-call' &&
                callIdRef.current === info.callId &&
                peersRef.current.size === 0) {
                teardown();
            }
        }, 8_000);
    }, [ensureLocalStream, clearRejoin, markOngoing, teardown]);
    const rejoinCall = useCallback(async () => {
        const info = rejoinInfoRef.current;
        if (!info)
            return;
        await joinExistingCall(info);
    }, [joinExistingCall]);
    /** Join the ongoing room call discovered via probe/beacon. */
    const joinOngoingCall = useCallback(async () => {
        const info = ongoingCallRef.current;
        if (!info)
            return;
        await joinExistingCall(info);
    }, [joinExistingCall]);
    /** Tell peers (one or all) our intentional local media state — disabled
     *  tracks still "send" black frames / silence, so without this signal the
     *  others cannot tell a switched-off camera/mic from a broken link. */
    const sendMediaState = useCallback((to) => {
        if (callStateRef.current !== 'in-call' || !callIdRef.current)
            return;
        sendRef.current({
            subtype: 'media-state',
            callId: callIdRef.current,
            ...(to ? { to } : {}),
            payload: { cameraOff: isCameraOffRef.current, micMuted: isMutedRef.current },
        });
    }, []);
    const toggleMute = useCallback(() => {
        setIsMuted((prev) => {
            const next = !prev;
            isMutedRef.current = next;
            // Any manual toggle makes the mute state the user's OWN choice — the
            // host's unmute-request must no longer touch it.
            mutedByHostRef.current = false;
            if (localStreamRef.current) {
                localStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = !next; });
            }
            sendMediaState();
            return next;
        });
    }, [sendMediaState]);
    /** Mute the local mic unconditionally (used for remote mute requests). */
    const muteSelf = useCallback(() => {
        if (isMutedRef.current)
            return;
        isMutedRef.current = true;
        setIsMuted(true);
        if (localStreamRef.current) {
            localStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = false; });
        }
        sendMediaState();
    }, [sendMediaState]);
    /** Ask every other participant to mute their microphone (host control).
     *  Each client mutes ITSELF on receipt — the tracks never leave the device,
     *  so this is a broadcast request, not a server-side enforcement. */
    const muteAll = useCallback(() => {
        if (callStateRef.current !== 'in-call' || !callIdRef.current)
            return;
        setMuteAllActive(true);
        sendRef.current({ subtype: 'mute-request', callId: callIdRef.current });
    }, []);
    /** Host control: undo a previous mute-request. Clients only comply when
     *  their mute originated from the host's request — a user's own mute (or a
     *  manual re-mute after the request) is never overridden remotely. */
    const unmuteAll = useCallback(() => {
        console.debug(`[voiceCall] unmuteAll pressed: state=${callStateRef.current}, callId=${callIdRef.current || '(none)'}`);
        if (callStateRef.current !== 'in-call' || !callIdRef.current)
            return;
        console.debug(`[voiceCall] sending unmute-request (callId=${callIdRef.current})`);
        setMuteAllActive(false);
        sendRef.current({ subtype: 'unmute-request', callId: callIdRef.current });
    }, []);
    /** Host control: targeted mute/unmute of ONE participant. Uses the same
     *  request subtypes with a `to` address — only the target complies. */
    const setParticipantMuted = useCallback((targetDeviceId, muted) => {
        if (callStateRef.current !== 'in-call' || !callIdRef.current)
            return;
        // Distinct subtypes: old clients treat every *-request as broadcast (they
        // have no `to` guard), so reusing them would mute the whole room. Old
        // clients simply ignore the unknown *-one subtypes instead.
        sendRef.current({
            subtype: muted ? 'mute-one' : 'unmute-one',
            callId: callIdRef.current,
            to: targetDeviceId,
        });
        // If this unmute leaves nobody else muted, the room is effectively back
        // to "unmuted" — flip the global mute-all toggle off so its state matches
        // reality (as if unmute-all had been pressed).
        if (!muted) {
            const othersStillMuted = [...remoteMicMutedRef.current].some((id) => id !== targetDeviceId);
            if (!othersStillMuted)
                setMuteAllActive(false);
        }
    }, []);
    /** Raise/lower own hand — a courtesy broadcast every participant renders. */
    const toggleHand = useCallback(() => {
        if (callStateRef.current !== 'in-call' || !callIdRef.current)
            return;
        const next = !handRaisedRef.current;
        handRaisedRef.current = next;
        setHandRaised(next);
        sendRef.current({ subtype: 'hand', callId: callIdRef.current, payload: { raised: next } });
    }, []);
    // The hand signal is transient (no relay replay): whenever the set of
    // participants changes (someone joined/rejoined), re-announce our raised
    // hand so late joiners see it too.
    const participantIdsKey = participants.map((p) => p.deviceId).sort().join(',');
    useEffect(() => {
        if (handRaisedRef.current && callIdRef.current && callStateRef.current === 'in-call') {
            sendRef.current({ subtype: 'hand', callId: callIdRef.current, payload: { raised: true } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [participantIdsKey]);
    /** Host control: remove a participant from the call. The target ends the
     *  call locally on receipt; the relay drops this subtype from guest sockets
     *  (same guard as mute-request). */
    const kickParticipant = useCallback((targetDeviceId) => {
        if (callStateRef.current !== 'in-call' || !callIdRef.current)
            return;
        sendRef.current({ subtype: 'kick', callId: callIdRef.current, to: targetDeviceId });
    }, []);
    const toggleCamera = useCallback(() => {
        setIsCameraOff((prev) => {
            const next = !prev;
            isCameraOffRef.current = next;
            // During a screen share the outgoing stream contains the display
            // capture. Toggle only the retained camera preview so this control can
            // never blank or replace the screen sender.
            const cameraStream = isScreenSharingRef.current
                ? cameraPreviewStreamRef.current
                : localStreamRef.current;
            if (cameraStream) {
                cameraStream.getVideoTracks().forEach((t) => { t.enabled = !next; });
            }
            sendMediaState();
            return next;
        });
    }, [sendMediaState]);
    /**
     * Switch between front and back camera on mobile. Acquires a new video track
     * with the opposite facingMode and hot-swaps it into every peer connection via
     * sender.replaceTrack() — no renegotiation needed. The self-preview updates
     * automatically because the track is replaced in-place on the same MediaStream.
     */
    const switchCamera = useCallback(async () => {
        const local = localStreamRef.current;
        if (!local || !isVideoCallRef.current)
            return;
        // While screen sharing, the outgoing video track is the screen — switching
        // cameras would silently replace it and desync the sharing state.
        if (isScreenSharingRef.current)
            return;
        const nextFacing = facingRef.current === 'user' ? 'environment' : 'user';
        let newStream;
        try {
            newStream = await navigator.mediaDevices.getUserMedia({
                video: cameraConstraintsForCurrentTier(nextFacing),
                audio: false,
            });
        }
        catch (err) {
            console.error('[voiceCall] switchCamera failed:', err);
            return;
        }
        // The call may have ended (hangup/teardown/unmount) during the await above.
        // If so, discard the freshly acquired stream so the camera does not stay on.
        if (localStreamRef.current !== local || !isVideoCallRef.current) {
            newStream.getTracks().forEach((t) => t.stop());
            return;
        }
        const newTrack = newStream.getVideoTracks()[0];
        if (!newTrack) {
            newStream.getTracks().forEach((t) => t.stop());
            return;
        }
        newTrack.enabled = !isCameraOffRef.current;
        // hot-swap the outgoing track in every peer connection
        peersRef.current.forEach((pc) => {
            const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'video');
            if (sender)
                sender.replaceTrack(newTrack).catch((err) => console.error('[voiceCall] replaceTrack failed:', err));
        });
        // swap the track in the local stream (keeps the same MediaStream object)
        const oldTrack = local.getVideoTracks()[0];
        if (oldTrack) {
            local.removeTrack(oldTrack);
            oldTrack.stop();
        }
        local.addTrack(newTrack);
        facingRef.current = nextFacing;
        // re-apply quality caps to the swapped-in track
        applyVideoQuality();
    }, [applyVideoQuality, cameraConstraintsForCurrentTier]);
    /**
     * Start/stop screen sharing (video calls only). Uses the same hot-swap
     * pattern as switchCamera: the screen-capture video track replaces the
     * camera track in every peer connection via sender.replaceTrack() — no
     * renegotiation. Stopping (via the button or the browser's own "Stop
     * sharing" bar) re-acquires the camera and swaps it back in.
     */
    const swapLocalVideoTrack = useCallback(async (local, newTrack, isCurrent) => {
        // Await all replaceTrack calls before stopping the old track so that
        // senders never momentarily carry a stopped track.
        const replacements = [];
        const replacePromises = [];
        peersRef.current.forEach((pc) => {
            const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'video');
            if (sender) {
                replacements.push({ sender, previousTrack: sender.track });
                replacePromises.push(sender.replaceTrack(newTrack).catch((err) => console.error('[voiceCall] replaceTrack failed:', err)));
            }
        });
        await Promise.all(replacePromises);
        if (!isCurrent()) {
            // The call/ownership changed while replaceTrack was pending. Restore
            // every sender to the track it had before the attempted swap; otherwise
            // it would remain attached to the now-stopped candidate screen track.
            await Promise.all(replacements.map(({ sender, previousTrack }) => sender.replaceTrack(previousTrack).catch(() => { })));
            if (pendingScreenTrackRef.current === newTrack) {
                pendingScreenTrackRef.current = null;
                newTrack.stop();
            }
            return false;
        }
        const oldTrack = local.getVideoTracks()[0];
        if (oldTrack) {
            local.removeTrack(oldTrack);
            oldTrack.stop();
        }
        local.addTrack(newTrack);
        // While sharing, local contains the outgoing screen track. Never expose it
        // as the self-preview: publish the independently cloned camera stream.
        // The stop path publishes local again only after every sender has the live
        // camera track.
        setLocalStream(isScreenSharingRef.current ? cameraPreviewStreamRef.current : local);
        applyVideoQuality();
        return true;
    }, [applyVideoQuality]);
    const releaseScreenShareOwnership = useCallback(() => {
        const activeCallId = callIdRef.current;
        if (activeCallId && screenShareOwnerRef.current === deviceIdRef.current) {
            sendRef.current({ subtype: 'screen-share-release', callId: activeCallId });
            screenShareOwnerByCallRef.current.set(activeCallId, null);
            screenShareOwnerRef.current = null;
            setScreenShareOwnerId(null);
            setParticipants((prev) => prev.map((participant) => ({ ...participant, isScreenSharing: false })));
        }
    }, []);
    const stopScreenShare = useCallback(async () => {
        const local = localStreamRef.current;
        if (!local || !isScreenSharingRef.current)
            return;
        // Prevent duplicate re-acquisitions: both the onended callback and a
        // UI-driven stop may fire in the same tick; only the first one runs.
        if (screenShareTransitionRef.current)
            return;
        screenShareTransitionRef.current = true;
        const operation = ++screenShareOperationRef.current;
        const isCurrent = () => screenShareOperationRef.current === operation &&
            localStreamRef.current === local &&
            isVideoCallRef.current;
        const finishTransition = () => {
            if (screenShareOperationRef.current === operation) {
                screenShareTransitionRef.current = false;
            }
        };
        const clearScreenTrack = async () => {
            const replacePromises = [];
            peersRef.current.forEach((pc) => {
                const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'video');
                if (sender)
                    replacePromises.push(sender.replaceTrack(null).catch(() => { }));
            });
            await Promise.all(replacePromises);
            if (!isCurrent())
                return;
            const screenTrack = local.getVideoTracks()[0];
            if (screenTrack) {
                local.removeTrack(screenTrack);
                screenTrack.stop();
            }
            setLocalStream(null);
            isScreenSharingRef.current = false;
            setIsScreenSharing(false);
        };
        // Silence the onended handler before the restoration starts so a browser
        // "Stop sharing" event cannot enqueue a second restoration. Disable the
        // sender's current track immediately so no more screen pixels are sent
        // while camera permission is pending, but keep that track object attached
        // until replaceTrack(camera) resolves. Stopping it first leaves mobile
        // receivers on an ended sender and some Safari/Android combinations do
        // not recover until the camera is toggled.
        const currentScreenTrack = local.getVideoTracks()[0];
        if (currentScreenTrack) {
            currentScreenTrack.onended = null;
            currentScreenTrack.enabled = false;
        }
        stopCameraPreview();
        // Hide the self-preview while camera permission is being reacquired. The
        // disabled sender remains identifiable until swapLocalVideoTrack replaces
        // it, so the remote tile never has to recover from a stopped sender.
        setLocalStream(null);
        isScreenSharingRef.current = false;
        setIsScreenSharing(false);
        let camStream;
        try {
            camStream = await navigator.mediaDevices.getUserMedia({
                video: cameraConstraintsForCurrentTier(facingRef.current),
                audio: false,
            });
        }
        catch (err) {
            console.error('[voiceCall] camera re-acquire after screen share failed:', err);
            // Camera is unavailable — stop sending the screen and update state.
            await clearScreenTrack();
            finishTransition();
            releaseScreenShareOwnership();
            return;
        }
        if (!isCurrent()) {
            camStream.getTracks().forEach((t) => t.stop());
            finishTransition();
            return;
        }
        const camTrack = camStream.getVideoTracks()[0];
        if (!camTrack) {
            camStream.getTracks().forEach((t) => t.stop());
            await clearScreenTrack();
            finishTransition();
            releaseScreenShareOwnership();
            return;
        }
        // Preserve the camera-off state: if the user had toggled camera off
        // before screen-sharing, restore that intent on the re-acquired track.
        camTrack.enabled = !isCameraOffRef.current;
        const swapped = await swapLocalVideoTrack(local, camTrack, isCurrent);
        if (!swapped) {
            finishTransition();
            return;
        }
        isScreenSharingRef.current = false;
        setIsScreenSharing(false);
        finishTransition();
        releaseScreenShareOwnership();
    }, [cameraConstraintsForCurrentTier, releaseScreenShareOwnership, stopCameraPreview, swapLocalVideoTrack]);
    const startScreenShareAfterClaim = useCallback(async () => {
        const local = localStreamRef.current;
        if (!local ||
            !isVideoCallRef.current ||
            screenShareOwnerRef.current !== deviceIdRef.current ||
            screenShareTransitionRef.current)
            return;
        if (!navigator.mediaDevices || !('getDisplayMedia' in navigator.mediaDevices)) {
            console.warn('[voiceCall] getDisplayMedia not supported on this device');
            releaseScreenShareOwnership();
            return;
        }
        screenShareTransitionRef.current = true;
        const operation = ++screenShareOperationRef.current;
        const transitionCallId = callIdRef.current;
        const isCurrent = () => screenShareOperationRef.current === operation &&
            localStreamRef.current === local &&
            isVideoCallRef.current &&
            callIdRef.current === transitionCallId &&
            screenShareOwnerRef.current === deviceIdRef.current;
        const finishTransition = () => {
            if (screenShareOperationRef.current === operation) {
                screenShareTransitionRef.current = false;
            }
        };
        let screenStream;
        // Keep a camera-only preview alive while the outgoing mesh sender is
        // replaced by the display track. Cloning is local to the existing capture
        // and does not request another camera permission.
        stopCameraPreview();
        const operationPreview = createCameraPreview(local);
        // Remove the camera element's source before the browser capture picker
        // resolves. This closes the capture-frame race for a selected monitor or
        // window; once sharing is active the state points at cameraPreview instead.
        setLocalStream(null);
        try {
            // Ask Chromium and other supporting browsers not to offer this tab as a
            // capture source. This avoids the common "hall of mirrors" path where
            // the call's own preview is captured into the shared tab. It cannot
            // remove a preview from an independently selected monitor/window; the UI
            // also omits the local preview once sharing is active for that case.
            screenStream = await navigator.mediaDevices.getDisplayMedia({
                video: true,
                audio: false,
                selfBrowserSurface: 'exclude',
            });
        }
        catch (err) {
            // user cancelled the picker — not an error worth surfacing
            const operationStillCurrent = isCurrent();
            stopCameraPreview(operationPreview);
            const hasLiveCamera = local.getVideoTracks().some((track) => track.readyState !== 'ended');
            if (localStreamRef.current === local && isVideoCallRef.current && hasLiveCamera) {
                setLocalStream(local);
            }
            finishTransition();
            if (operationStillCurrent)
                releaseScreenShareOwnership();
            return;
        }
        // The call may have ended during the picker — do not leak the capture.
        if (!isCurrent()) {
            screenStream.getTracks().forEach((t) => t.stop());
            stopCameraPreview(operationPreview);
            const hasLiveCamera = local.getVideoTracks().some((track) => track.readyState !== 'ended');
            if (localStreamRef.current === local && isVideoCallRef.current && hasLiveCamera) {
                setLocalStream(local);
            }
            finishTransition();
            return;
        }
        const screenTrack = screenStream.getVideoTracks()[0];
        if (!screenTrack) {
            screenStream.getTracks().forEach((t) => t.stop());
            stopCameraPreview(operationPreview);
            if (local.getVideoTracks().some((track) => track.readyState !== 'ended')) {
                setLocalStream(local);
            }
            finishTransition();
            releaseScreenShareOwnership();
            return;
        }
        // Screen content should always be visible, regardless of camera-off state.
        screenTrack.enabled = true;
        pendingScreenTrackRef.current = screenTrack;
        // Browser "Stop sharing" bar ends the track → revert to the camera.
        // If this happens while replaceTrack is still pending, remember it: the
        // transition lock intentionally blocks stopScreenShare until the start
        // swap settles, so the restore must be replayed immediately afterwards.
        let startTransitionSettled = false;
        let endedDuringStart = false;
        screenTrack.onended = () => {
            if (!startTransitionSettled) {
                endedDuringStart = true;
                return;
            }
            void stopScreenShare();
        };
        // Mark before the swap so applyVideoQuality gives the screen sender its
        // dedicated high/readable policy immediately.
        isScreenSharingRef.current = true;
        const swapped = await swapLocalVideoTrack(local, screenTrack, isCurrent);
        if (pendingScreenTrackRef.current === screenTrack) {
            pendingScreenTrackRef.current = null;
        }
        startTransitionSettled = true;
        if (!swapped) {
            stopCameraPreview(operationPreview);
            // A late replaceTrack completion may belong to a disposed call. Do not
            // clear the next call's sharing state from that stale operation.
            if (localStreamRef.current === local &&
                isVideoCallRef.current &&
                callIdRef.current === transitionCallId) {
                isScreenSharingRef.current = false;
                setIsScreenSharing(false);
            }
            finishTransition();
            return;
        }
        isScreenSharingRef.current = true;
        setIsScreenSharing(true);
        finishTransition();
        if (endedDuringStart) {
            await stopScreenShare();
        }
    }, [createCameraPreview, releaseScreenShareOwnership, stopCameraPreview, stopScreenShare, swapLocalVideoTrack]);
    startScreenShareAfterClaimRef.current = startScreenShareAfterClaim;
    const toggleScreenShare = useCallback(async () => {
        // Serialize: ignore rapid double-taps while a claim or media transition is in progress.
        if (screenShareTransitionRef.current || screenShareClaimPendingRef.current)
            return;
        if (isScreenSharingRef.current) {
            await stopScreenShare();
            return;
        }
        if (!localStreamRef.current || !isVideoCallRef.current || !callIdRef.current)
            return;
        if (screenShareOwnerRef.current &&
            screenShareOwnerRef.current !== deviceIdRef.current)
            return;
        if (screenShareOwnerRef.current === deviceIdRef.current) {
            await startScreenShareAfterClaim();
            return;
        }
        screenShareClaimPendingRef.current = true;
        setScreenShareClaimPending(true);
        sendRef.current({ subtype: 'screen-share-claim', callId: callIdRef.current });
        if (screenShareClaimTimerRef.current)
            clearTimeout(screenShareClaimTimerRef.current);
        screenShareClaimTimerRef.current = setTimeout(() => {
            screenShareClaimTimerRef.current = null;
            screenShareClaimPendingRef.current = false;
            setScreenShareClaimPending(false);
        }, 5000);
    }, [startScreenShareAfterClaim, stopScreenShare]);
    // A signaling disconnect invalidates the relay lock. Stop any still-running
    // capture immediately so the server may safely grant another participant
    // after it observes this socket closing.
    useEffect(() => {
        if (isConnected)
            return;
        if (screenShareClaimTimerRef.current) {
            clearTimeout(screenShareClaimTimerRef.current);
            screenShareClaimTimerRef.current = null;
        }
        screenShareClaimPendingRef.current = false;
        setScreenShareClaimPending(false);
        if (isScreenSharingRef.current)
            void stopScreenShare();
        const pendingScreenTrack = pendingScreenTrackRef.current;
        pendingScreenTrackRef.current = null;
        if (pendingScreenTrack) {
            pendingScreenTrack.onended = null;
            pendingScreenTrack.stop();
        }
        if (callIdRef.current)
            screenShareOwnerByCallRef.current.set(callIdRef.current, null);
        screenShareOwnerRef.current = null;
        setScreenShareOwnerId(null);
        setParticipants((prev) => prev.map((participant) => ({ ...participant, isScreenSharing: false })));
    }, [isConnected, stopScreenShare]);
    /**
     * Cycle to the next available microphone. Same hot-swap pattern as
     * switchCamera: acquire a new audio track from the next audioinput device and
     * replace it in every peer connection via sender.replaceTrack() — no
     * renegotiation. Mute state is preserved on the new track.
     */
    const switchMicrophone = useCallback(async (deviceId) => {
        const generation = ++micSwitchGenerationRef.current;
        const local = localStreamRef.current;
        if (!local)
            return;
        const currentId = micDeviceIdRef.current ||
            local.getAudioTracks()[0]?.getSettings().deviceId ||
            '';
        let targetId = deviceId;
        if (!targetId) {
            // No explicit device requested — cycle to the next available microphone.
            let mics;
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                mics = devices.filter((d) => d.kind === 'audioinput' && d.deviceId);
            }
            catch (err) {
                console.error('[voiceCall] enumerateDevices failed:', err);
                return;
            }
            if (mics.length < 2)
                return; // nothing to switch to
            const idx = mics.findIndex((d) => d.deviceId === currentId);
            targetId = mics[(idx + 1) % mics.length].deviceId;
        }
        if (!targetId || (targetId === currentId && targetId !== 'default'))
            return;
        if (generation !== micSwitchGenerationRef.current || localStreamRef.current !== local)
            return;
        let newStream;
        try {
            newStream = await navigator.mediaDevices.getUserMedia({
                audio: targetId === 'default' ? true : { deviceId: { exact: targetId } },
                video: false,
            });
        }
        catch (err) {
            if (generation !== micSwitchGenerationRef.current || localStreamRef.current !== local || callStateRef.current === 'idle')
                return;
            console.error('[voiceCall] switchMicrophone failed:', err);
            window.dispatchEvent(new Event("call-microphone-route-error"));
            return;
        }
        // The call may have ended during the await above — do not leak the mic.
        if (generation !== micSwitchGenerationRef.current || localStreamRef.current !== local || callStateRef.current === 'idle') {
            newStream.getTracks().forEach((t) => t.stop());
            return;
        }
        const newTrack = newStream.getAudioTracks()[0];
        if (!newTrack) {
            newStream.getTracks().forEach((t) => t.stop());
            return;
        }
        newTrack.enabled = !isMutedRef.current;
        peersRef.current.forEach((pc) => {
            const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'audio');
            if (sender)
                sender.replaceTrack(newTrack).catch((err) => console.error('[voiceCall] replaceTrack(audio) failed:', err));
        });
        const oldTrack = local.getAudioTracks()[0];
        if (oldTrack) {
            local.removeTrack(oldTrack);
            oldTrack.stop();
        }
        local.addTrack(newTrack);
        if (cameraPreviewStreamRef.current) {
            const previewAudio = cameraPreviewStreamRef.current.getAudioTracks()[0];
            if (previewAudio) {
                cameraPreviewStreamRef.current.removeTrack(previewAudio);
            }
            cameraPreviewStreamRef.current.addTrack(newTrack);
        }
        micDeviceIdRef.current = targetId;
    }, []);
    // --- signaling handler ---
    const handleSignal = useCallback((signal) => {
        const { subtype, callId: sigCallId, from } = signal;
        switch (subtype) {
            case 'invite': {
                if (callStateRef.current === 'idle') {
                    if (rejoinInfoRef.current && rejoinInfoRef.current.callId !== sigCallId) {
                        clearRejoin();
                    }
                    const video = !!(signal.payload && signal.payload.video);
                    incomingVideoRef.current = video;
                    setIncomingVideo(video);
                    setCallIdBoth(sigCallId);
                    setIncomingFrom(from);
                    setCallStateBoth('incoming');
                }
                else if (callStateRef.current === 'in-call' &&
                    callIdRef.current &&
                    sigCallId !== callIdRef.current) {
                    // Another call was started while ours is running. Only ONE call may
                    // exist in the room — deterministic rule: the smaller callId wins.
                    if (sigCallId < callIdRef.current) {
                        const video = !!(signal.payload && signal.payload.video);
                        void mergeIntoCall(sigCallId, video);
                    }
                    else {
                        // ours wins: tell the new caller to merge into our call
                        sendRef.current({
                            subtype: 'call-exists',
                            callId: callIdRef.current,
                            to: from,
                            payload: { video: isVideoCallRef.current },
                        });
                    }
                }
                break;
            }
            case 'call-exists': {
                // Another call is already running in the room. Same deterministic
                // rule: the smaller callId wins — the losing call merges into it
                // (mergeIntoCall also propagates the merge to our current peers).
                if (callStateRef.current === 'in-call' &&
                    callIdRef.current &&
                    sigCallId !== callIdRef.current) {
                    if (sigCallId < callIdRef.current) {
                        const video = !!(signal.payload && signal.payload.video);
                        void mergeIntoCall(sigCallId, video);
                    }
                    else {
                        sendRef.current({
                            subtype: 'call-exists',
                            callId: callIdRef.current,
                            to: from,
                            payload: { video: isVideoCallRef.current },
                        });
                    }
                }
                break;
            }
            case 'join': {
                // a newcomer joined a call we are in. Announce our presence so the
                // newcomer can discover us, then run deterministic offer initiation.
                if (callStateRef.current === 'in-call' && sigCallId === callIdRef.current) {
                    sendRef.current({ subtype: 'present', callId: sigCallId, to: from });
                    if (isCameraOffRef.current || isMutedRef.current) {
                        sendMediaState(from);
                    }
                    maybeInitiate(from);
                }
                else if (callStateRef.current === 'incoming' && sigCallId === callIdRef.current) {
                    // We are still ringing (or waiting for camera/mic permission in
                    // acceptCall). Remember the joiner and greet them after accept.
                    pendingJoinersRef.current.add(from);
                }
                else if (callStateRef.current === 'idle') {
                    // A presence beacon from a call we are not part of: remember that a
                    // call is ongoing so the UI can offer joining it. Beacon joins carry
                    // the call's video flag in the payload.
                    if (rejoinInfoRef.current && rejoinInfoRef.current.callId !== sigCallId) {
                        clearRejoin();
                    }
                    markOngoing({ callId: sigCallId, video: !!(signal.payload && signal.payload.video) });
                }
                break;
            }
            case 'call-probe': {
                // An idle device asks whether a call is running in the room.
                if (callStateRef.current === 'in-call' && callIdRef.current) {
                    sendRef.current({
                        subtype: 'call-info',
                        callId: callIdRef.current,
                        to: from,
                        payload: { video: isVideoCallRef.current },
                    });
                }
                break;
            }
            case 'call-info': {
                // Answer to our probe: a call is running in the room.
                if (callStateRef.current === 'idle') {
                    if (rejoinInfoRef.current && rejoinInfoRef.current.callId !== sigCallId) {
                        clearRejoin();
                    }
                    markOngoing({ callId: sigCallId, video: !!(signal.payload && signal.payload.video) });
                }
                break;
            }
            case 'present': {
                // an existing participant announced itself to us (we are the newcomer)
                if (callStateRef.current === 'in-call' && sigCallId === callIdRef.current) {
                    maybeInitiate(from);
                }
                break;
            }
            case 'offer': {
                if (callStateRef.current === 'in-call' && sigCallId === callIdRef.current) {
                    (async () => {
                        const pc = createPeer(from);
                        try {
                            await pc.setRemoteDescription(new RTCSessionDescription(signal.payload));
                            await flushCandidates(from);
                            const answer = await pc.createAnswer();
                            await pc.setLocalDescription(answer);
                            sendRef.current({ subtype: 'answer', callId: sigCallId, to: from, payload: answer });
                        }
                        catch (err) {
                            console.error('[voiceCall] handle offer failed:', err);
                        }
                    })();
                }
                break;
            }
            case 'answer': {
                if (sigCallId !== callIdRef.current)
                    break;
                const pc = peersRef.current.get(from);
                if (pc) {
                    (async () => {
                        try {
                            await pc.setRemoteDescription(new RTCSessionDescription(signal.payload));
                            await flushCandidates(from);
                        }
                        catch (err) {
                            console.error('[voiceCall] handle answer failed:', err);
                        }
                    })();
                }
                break;
            }
            case 'ice': {
                if (sigCallId !== callIdRef.current)
                    break;
                const pc = peersRef.current.get(from);
                const candidate = signal.payload;
                if (pc && pc.remoteDescription && pc.remoteDescription.type) {
                    pc.addIceCandidate(candidate).catch((err) => console.error('[voiceCall] addIceCandidate failed:', err));
                }
                else {
                    // remote description not set yet → queue
                    const q = pendingCandidatesRef.current.get(from) || [];
                    q.push(candidate);
                    pendingCandidatesRef.current.set(from, q);
                }
                break;
            }
            case 'reconnect': {
                // The other side detected a broken/stuck link and rebuilt its end.
                // Mirror it: drop our (dead) connection and renegotiate from scratch.
                if (callStateRef.current === 'in-call' && sigCallId === callIdRef.current) {
                    closePeer(from);
                    // fresh health baseline so our watchdog gives the incoming
                    // renegotiation time before stepping in again
                    peerHealthRef.current.set(from, freshHealth(peerHealthRef.current.get(from)));
                    sendRef.current({ subtype: 'present', callId: sigCallId, to: from });
                    if (isCameraOffRef.current || isMutedRef.current) {
                        sendMediaState(from);
                    }
                    void maybeInitiate(from);
                }
                break;
            }
            case 'ghost-kick': {
                // A peer determined that no media from us EVER arrived (we are a
                // "ghost" — e.g. our camera/mic is held by another window or the tab
                // was suspended). We are being removed from the call: end it locally
                // and tell the user the connection could not be established.
                // Addressed (`to`) — ONLY the target may react. Without this check a
                // single ghost-kick aimed at one peer tore down EVERYONE in the room.
                if (callStateRef.current === 'in-call' &&
                    sigCallId === callIdRef.current &&
                    signal.to === deviceIdRef.current) {
                    // Only the LINK to the sender is proven broken — the sender saw no
                    // media from us, but our connections to everyone else may be fine.
                    // Ending the whole call here dropped a healthy participant out of a
                    // group call because ONE peer lost them. Instead: drop just that
                    // link; the presence beacon rediscovers the pair if it recovers.
                    // Guard against a FALSE ghost-kick (e.g. an outdated client whose
                    // watchdog still treats a muted-but-alive peer as dead): if media
                    // from the sender reached US recently, the link is demonstrably
                    // alive — we are no ghost. Do not tear the call down; rebuild the
                    // link instead and let discovery re-pair us.
                    const h = peerHealthRef.current.get(from);
                    const kickerMicMuted = remoteMicMutedRef.current.has(from);
                    const recentMedia = shouldIgnoreGhostKick({
                        everHadMedia: h?.everHadMedia ?? false,
                        lastMediaAt: h?.lastMediaAt ?? null,
                        now: Date.now(),
                        recentWindowMs: NO_MEDIA_TIMEOUT_MS,
                        kickerMicMuted,
                    });
                    if (recentMedia) {
                        console.warn(`[voiceCall] ignoring ghost-kick from ${from} — ${kickerMicMuted ? 'sender is muted (alive by definition)' : 'media from it flowed recently'}, rebuilding the link instead`);
                        closePeer(from);
                        peerHealthRef.current.set(from, freshHealth(peerHealthRef.current.get(from)));
                        sendRef.current({ subtype: 'present', callId: sigCallId, to: from });
                        if (isCameraOffRef.current || isMutedRef.current) {
                            sendMediaState(from);
                        }
                        void maybeInitiate(from);
                        break;
                    }
                    const disposition = ghostKickDisposition({
                        participants: participantsRef.current,
                        kickingPeer: from,
                    });
                    if (disposition === 'drop-link-only') {
                        console.warn(`[voiceCall] ghost-kicked by ${from} — dropping only that link, staying in call with the others`);
                        closePeer(from);
                        removeParticipant(from);
                    }
                    else {
                        console.warn(`[voiceCall] removed from call by ${from} (no media ever flowed from us)`);
                        dropFromCall(true);
                    }
                }
                break;
            }
            case 'leave':
            case 'hangup': {
                if (sigCallId === callIdRef.current) {
                    removeParticipant(from);
                    if (peersRef.current.size === 0 && callStateRef.current === 'in-call') {
                        teardown();
                    }
                }
                break;
            }
            case 'media-state': {
                // A peer explicitly toggled its camera/mic. Track it so the UI can
                // show "camera off"/"muted" and the watchdog skips video rebuilds.
                if (callStateRef.current === 'in-call' && sigCallId === callIdRef.current) {
                    const cameraOff = !!(signal.payload && signal.payload.cameraOff);
                    const micMuted = !!(signal.payload && signal.payload.micMuted);
                    if (micMuted) {
                        remoteMicMutedRef.current.add(from);
                    }
                    else {
                        remoteMicMutedRef.current.delete(from);
                    }
                    if (cameraOff) {
                        remoteCameraOffRef.current.add(from);
                    }
                    else {
                        if (remoteCameraOffRef.current.delete(from)) {
                            // fresh video baseline: give the re-enabled camera a full
                            // timeout window before the watchdog may flag missing video
                            const h = peerHealthRef.current.get(from);
                            if (h)
                                h.lastVideoAt = Date.now();
                        }
                    }
                    setParticipants((prev) => prev.map((p) => (p.deviceId === from ? { ...p, cameraOff, micMuted } : p)));
                }
                break;
            }
            case 'screen-share-state': {
                const nextOwner = typeof signal.payload?.ownerDeviceId === 'string'
                    ? signal.payload.ownerDeviceId
                    : null;
                const localWasOwner = screenShareOwnerRef.current === deviceIdRef.current;
                screenShareOwnerByCallRef.current.set(sigCallId, nextOwner);
                if (sigCallId !== callIdRef.current)
                    break;
                screenShareOwnerRef.current = nextOwner;
                setScreenShareOwnerId(nextOwner);
                setParticipants((prev) => prev.map((p) => ({ ...p, isScreenSharing: p.deviceId === nextOwner })));
                if (screenShareClaimPendingRef.current) {
                    if (screenShareClaimTimerRef.current) {
                        clearTimeout(screenShareClaimTimerRef.current);
                        screenShareClaimTimerRef.current = null;
                    }
                    screenShareClaimPendingRef.current = false;
                    setScreenShareClaimPending(false);
                    if (nextOwner === deviceIdRef.current) {
                        void startScreenShareAfterClaimRef.current?.();
                    }
                }
                // A server-authoritative owner change can revoke this device while
                // its capture is active (for example, after a signaling reconnect).
                // Disable/restore locally immediately; stopScreenShare does not
                // release a lock now held by the new remote owner.
                if (localWasOwner &&
                    nextOwner !== deviceIdRef.current &&
                    isScreenSharingRef.current) {
                    void stopScreenShare();
                }
                break;
            }
            case 'screen-share-claim-result': {
                if (sigCallId !== callIdRef.current || signal.payload?.granted !== false)
                    break;
                if (screenShareClaimTimerRef.current) {
                    clearTimeout(screenShareClaimTimerRef.current);
                    screenShareClaimTimerRef.current = null;
                }
                screenShareClaimPendingRef.current = false;
                setScreenShareClaimPending(false);
                break;
            }
            case 'mute-one': // selective: only the addressed target complies
            case 'mute-request': {
                if (signal.subtype === 'mute-one' && signal.to !== deviceIdRef.current)
                    break;
                // The host asked everyone (or just us, when addressed) to mute. We
                // mute OURSELVES (the request cannot touch our tracks — it is a
                // courtesy broadcast); the regular media-state signal then updates
                // our badge on every other client.
                if (signal.to && signal.to !== deviceIdRef.current)
                    break;
                if (callStateRef.current === 'in-call' && sigCallId === callIdRef.current) {
                    // Remember that THIS mute came from the host: only such a mute may
                    // be undone by the host's unmute-request later. An already muted
                    // mic stays the user's own choice.
                    if (!isMutedRef.current)
                        mutedByHostRef.current = true;
                    muteSelf();
                }
                break;
            }
            case 'unmute-one': // selective: only the addressed target complies
            case 'unmute-request': {
                if (signal.subtype === 'unmute-one' && signal.to !== deviceIdRef.current)
                    break;
                // The host withdrew its mute request (broadcast) or selectively
                // unmuted us (`to` = our deviceId). Comply ONLY if our mute was
                // caused by the host — a manual mute is never overridden.
                if (signal.to && signal.to !== deviceIdRef.current)
                    break;
                console.debug(`[voiceCall] unmute-request received from ${from}: state=${callStateRef.current}, callIdMatch=${sigCallId === callIdRef.current}, mutedByHost=${mutedByHostRef.current}, isMuted=${isMutedRef.current}`);
                if (callStateRef.current === 'in-call' &&
                    sigCallId === callIdRef.current &&
                    mutedByHostRef.current &&
                    isMutedRef.current) {
                    mutedByHostRef.current = false;
                    isMutedRef.current = false;
                    setIsMuted(false);
                    if (localStreamRef.current) {
                        localStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = true; });
                    }
                    sendMediaState();
                    // Being unmuted by the host fulfils a pending hand raise.
                    if (handRaisedRef.current && callIdRef.current) {
                        handRaisedRef.current = false;
                        setHandRaised(false);
                        sendRef.current({ subtype: 'hand', callId: callIdRef.current, payload: { raised: false } });
                    }
                }
                break;
            }
            case 'hand': {
                // a participant raised/lowered their hand — pure UI state
                if (callStateRef.current === 'in-call' && sigCallId === callIdRef.current && from) {
                    setRaisedHands((prev) => signal.payload?.raised
                        ? prev.includes(from) ? prev : [...prev, from]
                        : prev.filter((id) => id !== from));
                }
                break;
            }
            case 'kick': {
                // The host removed us from the call. Addressed (`to`) — only the
                // target reacts; everyone else sees the normal leave that follows.
                if (callStateRef.current === 'in-call' &&
                    sigCallId === callIdRef.current &&
                    signal.to === deviceIdRef.current) {
                    console.warn(`[voiceCall] removed from call by ${from} (host kick)`);
                    teardown();
                    clearRejoinEntry();
                    setCallDroppedByBackground(false);
                    setCallDropped(true);
                }
                break;
            }
            case 'reject': {
                // informational: a callee declined. In a 1:1 with nobody connected yet,
                // the caller can choose to end. We keep the call open for group cases.
                break;
            }
            default:
                break;
        }
    }, [createPeer, flushCandidates, removeParticipant, teardown, dropFromCall, maybeInitiate, closePeer, mergeIntoCall, markOngoing, clearRejoin, sendMediaState, muteSelf, wasRecentlyBackgrounded, stopScreenShare]);
    // Ongoing-call discovery: when we connect (or return to idle), ask the room
    // whether a call is already running so the UI can offer joining it — needed
    // for users who open the chat AFTER the call started and never saw the ring.
    useEffect(() => {
        if (!isConnected || callState !== 'idle')
            return;
        sendRef.current({ subtype: 'call-probe', callId: 'probe' });
    }, [isConnected, callState]);
    // Ongoing-call info expires when no beacon/probe answer refreshes it —
    // that means the call ended (or everyone left).
    useEffect(() => {
        if (callState !== 'idle' || !ongoingCall)
            return;
        const iv = setInterval(() => {
            if (Date.now() - ongoingSeenAtRef.current > ONGOING_CALL_TTL_MS) {
                ongoingCallRef.current = null;
                setOngoingCall(null);
            }
        }, ONGOING_CALL_CHECK_MS);
        return () => clearInterval(iv);
    }, [callState, ongoingCall]);
    // keep a ref mirror of participants for timer-based checks
    useEffect(() => {
        participantsRef.current = participants;
    }, [participants]);
    // Active-speaker detection: analyse each remote audio stream locally with
    // the Web Audio API (no signaling — works with E2EE, zero network cost) and
    // flag participants whose level crosses a speech threshold. The UI uses the
    // flag to highlight the talking tile.
    useEffect(() => {
        if (callState !== 'in-call')
            return;
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx)
            return;
        const ctx = new Ctx();
        const SPEAKING_RMS = 0.025;
        const analysers = new Map();
        const iv = setInterval(() => {
            if (ctx.state === 'suspended')
                ctx.resume().catch(() => { });
            const speakingNow = new Set();
            // drop analysers of peers that left the call (long-call leak guard)
            const present = new Set(participantsRef.current.map((p) => p.deviceId));
            analysers.forEach((e, id) => {
                if (!present.has(id)) {
                    try {
                        e.source.disconnect();
                    }
                    catch { /* already gone */ }
                    analysers.delete(id);
                }
            });
            participantsRef.current.forEach((p) => {
                const stream = p.stream;
                if (!stream || stream.getAudioTracks().length === 0)
                    return;
                let entry = analysers.get(p.deviceId);
                // the stream object is replaced on rebuilds — re-wire the analyser
                if (entry && entry.streamId !== stream.id) {
                    try {
                        entry.source.disconnect();
                    }
                    catch { /* already gone */ }
                    analysers.delete(p.deviceId);
                    entry = undefined;
                }
                if (!entry) {
                    try {
                        const source = ctx.createMediaStreamSource(stream);
                        const analyser = ctx.createAnalyser();
                        analyser.fftSize = 512;
                        source.connect(analyser);
                        entry = { analyser, source, streamId: stream.id, buf: new Uint8Array(analyser.fftSize) };
                        analysers.set(p.deviceId, entry);
                    }
                    catch {
                        return; // stream not analysable (yet)
                    }
                }
                const buf = entry.buf;
                entry.analyser.getByteTimeDomainData(buf);
                let sum = 0;
                for (let i = 0; i < buf.length; i++) {
                    const v = (buf[i] - 128) / 128;
                    sum += v * v;
                }
                if (Math.sqrt(sum / buf.length) > SPEAKING_RMS)
                    speakingNow.add(p.deviceId);
            });
            setParticipants((prev) => {
                let changed = false;
                const next = prev.map((p) => {
                    const s = speakingNow.has(p.deviceId);
                    if (!!p.speaking === s)
                        return p;
                    changed = true;
                    return { ...p, speaking: s };
                });
                return changed ? next : prev;
            });
            // 250ms feels instant on desktop; on mobile 500ms halves the WebAudio
            // wakeups (battery/heat) with no visible lag in the speaking highlight.
        }, isMobileDevice() ? 500 : 250);
        return () => {
            clearInterval(iv);
            analysers.forEach((e) => { try {
                e.source.disconnect();
            }
            catch { /* noop */ } });
            ctx.close().catch(() => { });
        };
    }, [callState]);
    // Connection watchdog: while in a call, keep checking every peer link and
    // rebuild it until picture and sound actually flow. Handles peers stuck in
    // 'connecting', long 'disconnected' phases, and "connected but no media"
    // cases that are common on mobile networks.
    useEffect(() => {
        if (callState !== 'in-call')
            return;
        let lastBeaconAt = Date.now();
        const iv = setInterval(() => {
            const now = Date.now();
            // Per-pass uplink samples: each peer's stats contribute at most one
            // sample; ONE adaptation decision is made per watchdog pass (below),
            // using the most pessimistic (minimum) sample.
            const uplinkSamples = [];
            const statsJobs = [];
            // Presence beacon: rebroadcast `join` periodically. If our original
            // `join` (or a peer's `present`) was lost — common on mobile when the
            // WebSocket drops during the permission prompt — this re-runs discovery.
            // Idempotent: peers that already have a live connection to us no-op.
            if (now - lastBeaconAt >= PRESENCE_BEACON_INTERVAL_MS && callIdRef.current) {
                lastBeaconAt = now;
                // payload.video lets idle devices show the right "join ongoing" icon
                sendRef.current({
                    subtype: 'join',
                    callId: callIdRef.current,
                    payload: { video: isVideoCallRef.current },
                });
            }
            // Iterate over PARTICIPANTS, not just live peer connections: after a
            // rebuild (or a lost `reconnect` signal) a participant may have no
            // RTCPeerConnection at all — those must keep being retried too.
            participantsRef.current.forEach(({ deviceId: remoteId }) => {
                let health = peerHealthRef.current.get(remoteId);
                if (!health) {
                    // participant without bookkeeping (e.g. we closed our pc while
                    // waiting for the other side) — start the clock now
                    health = freshHealth();
                    peerHealthRef.current.set(remoteId, health);
                    return;
                }
                // Deterministic driver (greater deviceId) acts first; the other side
                // steps in as a fallback if nothing happened for twice as long.
                const isDriver = deviceIdRef.current > remoteId;
                const factor = isDriver ? 1 : 2;
                // A peer that told us (via media-state) that its mic is muted is
                // EXPECTED to be silent on the wire — some browsers stop emitting RTP
                // entirely when the audio track is disabled, and during the mute
                // even video RTP may stall briefly. The explicit media-state signal
                // is proof of life, so such a peer must not be rebuilt for "no
                // media" and must NEVER be ghost-kicked — not even in a video call
                // with the camera nominally on. (Previously the exemption required
                // the camera to be off too; in a 2-person video call the host muting
                // the other side could then escalate to ghost-kick + full teardown,
                // dropping the ADMIN out of the call. Missing video is handled
                // separately below by the gentler video-rebuild branch, which only
                // renegotiates with backoff and never kicks.)
                const intentionallySilent = isIntentionallySilent({
                    remoteMicMuted: remoteMicMutedRef.current.has(remoteId),
                    isVideoCall: isVideoCallRef.current,
                    remoteCameraOff: remoteCameraOffRef.current.has(remoteId),
                });
                // Ghost removal: a peer that NEVER delivered a single media byte
                // within the ghost window (despite the watchdog's rebuild attempts —
                // CONNECT_TIMEOUT_MS guarantees at least one rebuild happened first)
                // is an empty tile: drop it from the grid and tell it why.
                if (!health.everHadMedia && !intentionallySilent && now - health.firstSeenAt > GHOST_TIMEOUT_MS * factor) {
                    console.warn(`[voiceCall] removing ghost participant ${remoteId} (no media ever within ${Math.round((now - health.firstSeenAt) / 1000)}s)`);
                    if (callIdRef.current) {
                        sendRef.current({ subtype: 'ghost-kick', callId: callIdRef.current, to: remoteId });
                    }
                    removeParticipant(remoteId);
                    const othersLeft = participantsRef.current.some((p) => p.deviceId !== remoteId);
                    if (!othersLeft && peersRef.current.size === 0 && callStateRef.current === 'in-call') {
                        dropFromCall(false);
                    }
                    return;
                }
                // exponential-ish backoff between rebuild attempts towards a peer
                const backoff = Math.min(RECONNECT_BACKOFF_BASE_MS * Math.max(1, health.rebuilds), RECONNECT_BACKOFF_MAX_MS);
                if (now - health.lastRebuildAt < backoff)
                    return;
                // Only one side drives the rebuild to avoid duplicate storms (see
                // isDriver/factor above).
                const pc = peersRef.current.get(remoteId);
                if (!pc) {
                    // no connection at all — renegotiation never arrived, retry
                    if (now - health.createdAt > CONNECT_TIMEOUT_MS * factor) {
                        rebuildPeer(remoteId, 'no connection');
                    }
                    return;
                }
                const state = pc.connectionState;
                if ((state === 'new' || state === 'connecting') &&
                    now - health.createdAt > CONNECT_TIMEOUT_MS * factor) {
                    rebuildPeer(remoteId, `stuck in ${state}`);
                    return;
                }
                if (state === 'disconnected' &&
                    health.disconnectedAt !== null &&
                    now - health.disconnectedAt > DISCONNECTED_TIMEOUT_MS * factor) {
                    rebuildPeer(remoteId, 'disconnected too long');
                    return;
                }
                if (state === 'connected' && health.connectedAt !== null) {
                    // Real media-liveness check: inbound RTP byte counters must keep
                    // growing. A track can report readyState 'live' while no packets
                    // flow (one-way media failure), so we look at getStats() instead.
                    const h = health;
                    statsJobs.push(pc.getStats()
                        .then((stats) => {
                        // ignore if the pc was replaced/closed meanwhile
                        if (peersRef.current.get(remoteId) !== pc)
                            return;
                        if (callStateRef.current !== 'in-call')
                            return;
                        let inboundBytes = 0;
                        let videoBytes = 0;
                        // Only the ACTIVE candidate pair's estimate counts — stale
                        // "succeeded" pairs keep an old (often higher) estimate that
                        // would mask a congested link.
                        let selectedPairId = null;
                        stats.forEach((report) => {
                            if (report.type === 'transport') {
                                const r = report;
                                if (r.selectedCandidatePairId)
                                    selectedPairId = r.selectedCandidatePairId;
                            }
                        });
                        stats.forEach((report) => {
                            if (report.type === 'inbound-rtp' && !report.isRemote) {
                                inboundBytes += report.bytesReceived ?? 0;
                                if (report.kind === 'video')
                                    videoBytes += report.bytesReceived ?? 0;
                            }
                            if (report.type === 'candidate-pair') {
                                const r = report;
                                const isActive = selectedPairId ? report.id === selectedPairId : r.nominated === true;
                                if (isActive && typeof r.availableOutgoingBitrate === 'number') {
                                    uplinkSamples.push(r.availableOutgoingBitrate);
                                }
                            }
                        });
                        const t = Date.now();
                        if (videoBytes > h.lastVideoBytes) {
                            h.lastVideoBytes = videoBytes;
                            h.lastVideoAt = t;
                        }
                        if (inboundBytes > h.lastInboundBytes) {
                            h.lastInboundBytes = inboundBytes;
                            h.lastMediaAt = t;
                            h.everHadMedia = true;
                        }
                        else if (intentionallySilent) {
                            // Muted mic + no video to send: silence is intentional, keep
                            // the liveness baseline fresh so unmuting doesn't instantly
                            // trip the no-media rebuild.
                            h.lastMediaAt = t;
                        }
                        else {
                            const sinceMedia = t - (h.lastMediaAt ?? h.connectedAt ?? t);
                            if (sinceMedia > NO_MEDIA_TIMEOUT_MS * factor) {
                                rebuildPeer(remoteId, 'connected but no media flowing');
                                return;
                            }
                        }
                        // Video call, audio flows, but the peer's video never arrives
                        // (or stopped): keep rebuilding with a growing backoff — but
                        // NOT if the peer explicitly told us its camera is off.
                        if (isVideoCallRef.current && !remoteCameraOffRef.current.has(remoteId)) {
                            const sinceVideo = t - (h.lastVideoAt ?? h.connectedAt ?? t);
                            const videoWait = NO_VIDEO_TIMEOUT_MS * factor * (1 + h.videoRebuilds);
                            if (sinceVideo > videoWait) {
                                rebuildPeer(remoteId, `video call but no video flowing (attempt ${h.videoRebuilds + 1})`, true);
                            }
                        }
                    })
                        .catch(() => { }));
                }
            });
            // One adaptation decision per watchdog pass, after every peer's stats
            // resolved: the weakest path (minimum estimate) drives the decision.
            if (statsJobs.length > 0) {
                Promise.allSettled(statsJobs).then(() => {
                    if (callStateRef.current !== 'in-call')
                        return;
                    adaptToUplink(uplinkSamples.length > 0 ? Math.min(...uplinkSamples) : null);
                });
            }
        }, WATCHDOG_INTERVAL_MS);
        return () => clearInterval(iv);
    }, [callState, rebuildPeer, dropFromCall, adaptToUplink]);
    // Persist the ongoing call in sessionStorage (refreshed periodically), so
    // an accidental page refresh offers "rejoin" instead of losing the call.
    useEffect(() => {
        if (callState !== 'in-call' || !callId)
            return;
        const save = () => saveRejoinEntry(callId, isVideoCallRef.current);
        save();
        const iv = setInterval(save, 30_000);
        return () => clearInterval(iv);
    }, [callState, callId]);
    // On mount: if we were in a call (e.g. the page was refreshed mid-call),
    // keep offering rejoin until the user joins/replaces it or the session ends.
    useEffect(() => {
        const entry = loadRejoinEntry();
        if (!entry)
            return;
        rejoinInfoRef.current = { callId: entry.callId, video: entry.video };
        setRejoinGeneration((value) => value + 1);
        setCanRejoin(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // clean up on unmount
    useEffect(() => {
        return () => {
            teardown({ preserveRejoinStorage: true });
            if (rejoinProbeTimerRef.current) {
                clearTimeout(rejoinProbeTimerRef.current);
                rejoinProbeTimerRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return {
        transportConnected,
        callState,
        participants,
        incomingFrom,
        callId,
        isMuted,
        isVideoCall,
        incomingVideo,
        isCameraOff,
        localStream,
        startCall,
        acceptCall,
        rejectCall,
        hangup,
        leaveCall,
        toggleMute,
        muteAll,
        unmuteAll,
        muteAllActive,
        setParticipantMuted,
        handRaised,
        toggleHand,
        raisedHands,
        kickParticipant,
        toggleCamera,
        switchCamera,
        switchMicrophone,
        isScreenSharing,
        screenShareDisabled: !isConnected ||
            screenShareClaimPending ||
            (!!screenShareOwnerId && screenShareOwnerId !== deviceId),
        toggleScreenShare,
        canRejoin,
        rejoinCallId: rejoinInfoRef.current?.callId ?? null,
        rejoinCallIdentity: rejoinInfoRef.current
            ? `mesh:${rejoinInfoRef.current.callId}:${rejoinGeneration}`
            : null,
        rejoinCall,
        ongoingCall,
        joinOngoingCall,
        callDropped,
        callDroppedByBackground,
        dismissCallDropped,
        handleSignal,
        fileTransfers,
        shareFile,
        dismissTransfer,
    };
}
